import { useEffect } from "react";
import { useLocation, matchRoutes } from "react-router-dom";
import { analytics, EVENTS } from "./index";
import { installClickTracker } from "./clickTracker";

// Mount once inside the router (AppWrapper). Sends page_view on every route change with the route
// pattern (e.g. /shop/:shopId/product/:id) so reports are not fragmented by ids, keeps the GA
// user_id in step with the logged-in user, and installs the global click and error tracking.
const MAX_ERRORS_PER_PAGE_LOAD = 5;

const patternFor = (routes, pathname) => {
  try {
    const matches = matchRoutes(routes, pathname);
    if (!matches) return pathname;
    const joined = matches
      .map((m) => m.route.path || "")
      .filter(Boolean)
      .join("/")
      .replace(/\/+/g, "/");
    return joined || pathname;
  } catch {
    return pathname;
  }
};

export default function RouteTracker({ routes }) {
  const location = useLocation();

  useEffect(() => {
    if (!analytics.isEnabled()) return;
    // The Google script is loaded lazily by the first tracked event (never on staff pages).
    installClickTracker();
    let errors = 0;
    const report = (description) => {
      if (errors++ >= MAX_ERRORS_PER_PAGE_LOAD) return;
      analytics.track(EVENTS.EXCEPTION, { description: String(description).slice(0, 100), fatal: false });
    };
    const onError = (e) => report(e.message || "error");
    const onRejection = (e) => report(e.reason?.message || "unhandled_rejection");
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  useEffect(() => {
    if (!analytics.isEnabled() || analytics.isStaffPage()) return;
    analytics.setUser(localStorage.getItem("userId") || null);
    analytics.pageView({
      pathname: location.pathname,
      search: location.search,
      patternPath: patternFor(routes, location.pathname),
    });
  }, [location.pathname, location.search, routes]);

  return null;
}
